import { useEffect, useState } from "react";
import { Clock, Plus, Trash2, Calendar as CalendarIcon, CalendarRange, Coffee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { toast } from "sonner";

const WEEKDAYS = [
  { key: "0", label: "Lunes" },
  { key: "1", label: "Martes" },
  { key: "2", label: "Miércoles" },
  { key: "3", label: "Jueves" },
  { key: "4", label: "Viernes" },
  { key: "5", label: "Sábado" },
  { key: "6", label: "Domingo" },
];

export default function ScheduleSettings() {
  const [hours, setHours] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [saving, setSaving] = useState(false);

  // New block form
  const [blockType, setBlockType] = useState("day");
  const [bDate, setBDate] = useState("");
  const [bStartDate, setBStartDate] = useState("");
  const [bEndDate, setBEndDate] = useState("");
  const [bStartTime, setBStartTime] = useState("12:00");
  const [bEndTime, setBEndTime] = useState("13:00");
  const [bReason, setBReason] = useState("");
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/schedule/config");
      setHours(data.business_hours);
      setBlocks(data.blocks || []);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => { load(); }, []);

  const updateDay = (key, field, value) => {
    setHours({ ...hours, [key]: { ...(hours?.[key] || {}), [field]: value } });
  };

  const saveHours = async () => {
    setSaving(true);
    try {
      await api.put("/schedule/config", { business_hours: hours });
      toast.success("Horario guardado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally {
      setSaving(false);
    }
  };

  const createBlock = async (e) => {
    e.preventDefault();
    const body = { type: blockType, reason: bReason || "" };
    if (blockType === "day") {
      if (!bDate) return toast.error("Selecciona la fecha");
      body.date = bDate;
    } else if (blockType === "range") {
      if (!bStartDate || !bEndDate) return toast.error("Selecciona ambas fechas");
      body.start_date = bStartDate;
      body.end_date = bEndDate;
    } else if (blockType === "slot") {
      if (!bDate || !bStartTime || !bEndTime) return toast.error("Completa fecha y horas");
      body.date = bDate;
      body.start_time = bStartTime;
      body.end_time = bEndTime;
    }
    setCreating(true);
    try {
      await api.post("/schedule/blocks", body);
      toast.success("Bloqueo creado");
      setBDate(""); setBStartDate(""); setBEndDate(""); setBReason("");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear");
    } finally {
      setCreating(false);
    }
  };

  const deleteBlock = async (id) => {
    try {
      await api.delete(`/schedule/blocks/${id}`);
      toast.success("Bloqueo eliminado");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  if (!hours) return null;

  return (
    <>
      {/* Business hours */}
      <div className="surface-card p-6" data-testid="schedule-hours">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[#B5704A]/10 flex items-center justify-center">
            <Clock className="w-6 h-6 text-[#B5704A]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-[600] text-[#3A2418]">Horario de atención</h3>
            <p className="text-sm text-[#6F5A4C] mt-1">
              Define qué días y horarios estás disponible. Los slots fuera de este rango no se ofrecen al reservar.
            </p>
            <div className="mt-5 space-y-2">
              {WEEKDAYS.map(({ key, label }) => {
                const d = hours[key] || { enabled: false, open: "09:00", close: "19:00" };
                return (
                  <div key={key} className="grid grid-cols-12 items-center gap-2 py-1" data-testid={`day-row-${key}`}>
                    <label className="col-span-3 sm:col-span-2 flex items-center gap-2 text-sm text-[#3A2418]">
                      <input
                        type="checkbox"
                        checked={d.enabled}
                        onChange={(e) => updateDay(key, "enabled", e.target.checked)}
                        className="w-4 h-4 accent-[#B5704A]"
                        data-testid={`day-enabled-${key}`}
                      />
                      {label}
                    </label>
                    <div className="col-span-4 sm:col-span-3">
                      <Input
                        type="time"
                        value={d.open}
                        disabled={!d.enabled}
                        onChange={(e) => updateDay(key, "open", e.target.value)}
                        className="h-9 bg-white border-[#E8DFD2] disabled:opacity-40"
                        data-testid={`day-open-${key}`}
                      />
                    </div>
                    <span className="col-span-1 text-center text-[#6F5A4C] text-sm">—</span>
                    <div className="col-span-4 sm:col-span-3">
                      <Input
                        type="time"
                        value={d.close}
                        disabled={!d.enabled}
                        onChange={(e) => updateDay(key, "close", e.target.value)}
                        className="h-9 bg-white border-[#E8DFD2] disabled:opacity-40"
                        data-testid={`day-close-${key}`}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              onClick={saveHours}
              disabled={saving}
              className="mt-4 rounded-full bg-[#3A2418] hover:bg-[#3A2418]/90 text-white"
              data-testid="save-hours-button"
            >
              {saving ? "Guardando…" : "Guardar horario"}
            </Button>
          </div>
        </div>
      </div>

      {/* Blocks */}
      <div className="surface-card p-6" data-testid="schedule-blocks">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[#B5704A]/10 flex items-center justify-center">
            <CalendarRange className="w-6 h-6 text-[#B5704A]" />
          </div>
          <div className="flex-1">
            <h3 className="font-display text-lg font-[600] text-[#3A2418]">Días y horas no disponibles</h3>
            <p className="text-sm text-[#6F5A4C] mt-1">
              Bloquea momentos específicos (un día libre, vacaciones, un almuerzo extendido).
            </p>

            {/* Existing blocks */}
            {blocks.length > 0 && (
              <ul className="mt-5 space-y-2" data-testid="blocks-list">
                {blocks.map((b) => (
                  <li key={b.id} className="flex items-center justify-between bg-[#F4EFE4] rounded-md px-4 py-3" data-testid={`block-${b.id}`}>
                    <div className="flex items-center gap-3 text-sm">
                      {b.type === "day" && <CalendarIcon className="w-4 h-4 text-[#B5704A]" />}
                      {b.type === "range" && <CalendarRange className="w-4 h-4 text-[#B5704A]" />}
                      {b.type === "slot" && <Coffee className="w-4 h-4 text-[#B5704A]" />}
                      <div>
                        <div className="text-[#3A2418]">
                          {b.type === "day" && `Día completo · ${b.date}`}
                          {b.type === "range" && `${b.start_date} a ${b.end_date}`}
                          {b.type === "slot" && `${b.date} · ${b.start_time}–${b.end_time}`}
                        </div>
                        {b.reason && <div className="text-xs text-[#6F5A4C]">{b.reason}</div>}
                      </div>
                    </div>
                    <button
                      onClick={() => deleteBlock(b.id)}
                      className="text-[#B5704A] hover:text-[#3A2418]"
                      data-testid={`delete-block-${b.id}`}
                      aria-label="Eliminar bloqueo"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}

            {/* New block form */}
            <form onSubmit={createBlock} className="mt-5 bg-white border border-[#E8DFD2] rounded-lg p-4 space-y-3" data-testid="new-block-form">
              <div className="flex gap-2 flex-wrap">
                {[
                  { v: "day", label: "Día completo", icon: CalendarIcon },
                  { v: "range", label: "Rango de fechas", icon: CalendarRange },
                  { v: "slot", label: "Franja puntual", icon: Coffee },
                ].map(({ v, label, icon: I }) => (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setBlockType(v)}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-colors ${
                      blockType === v ? "bg-[#3A2418] text-white border-[#3A2418]" : "bg-white text-[#6F5A4C] border-[#E8DFD2] hover:border-[#B5704A]"
                    }`}
                    data-testid={`block-type-${v}`}
                  >
                    <I className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>

              <div className="grid sm:grid-cols-2 gap-3">
                {blockType === "day" && (
                  <div className="sm:col-span-2">
                    <Label className="text-xs text-[#6F5A4C]">Fecha</Label>
                    <Input type="date" value={bDate} onChange={(e) => setBDate(e.target.value)} className="mt-1 h-10 bg-white border-[#E8DFD2]" data-testid="block-date-input" />
                  </div>
                )}
                {blockType === "range" && (
                  <>
                    <div>
                      <Label className="text-xs text-[#6F5A4C]">Desde</Label>
                      <Input type="date" value={bStartDate} onChange={(e) => setBStartDate(e.target.value)} className="mt-1 h-10 bg-white border-[#E8DFD2]" data-testid="block-start-date" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#6F5A4C]">Hasta</Label>
                      <Input type="date" value={bEndDate} onChange={(e) => setBEndDate(e.target.value)} className="mt-1 h-10 bg-white border-[#E8DFD2]" data-testid="block-end-date" />
                    </div>
                  </>
                )}
                {blockType === "slot" && (
                  <>
                    <div className="sm:col-span-2">
                      <Label className="text-xs text-[#6F5A4C]">Fecha</Label>
                      <Input type="date" value={bDate} onChange={(e) => setBDate(e.target.value)} className="mt-1 h-10 bg-white border-[#E8DFD2]" data-testid="block-slot-date" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#6F5A4C]">Desde hora</Label>
                      <Input type="time" value={bStartTime} onChange={(e) => setBStartTime(e.target.value)} className="mt-1 h-10 bg-white border-[#E8DFD2]" data-testid="block-start-time" />
                    </div>
                    <div>
                      <Label className="text-xs text-[#6F5A4C]">Hasta hora</Label>
                      <Input type="time" value={bEndTime} onChange={(e) => setBEndTime(e.target.value)} className="mt-1 h-10 bg-white border-[#E8DFD2]" data-testid="block-end-time" />
                    </div>
                  </>
                )}
                <div className="sm:col-span-2">
                  <Label className="text-xs text-[#6F5A4C]">Motivo (opcional)</Label>
                  <Input value={bReason} onChange={(e) => setBReason(e.target.value)} placeholder="Vacaciones, almuerzo, capacitación…" className="mt-1 h-10 bg-white border-[#E8DFD2]" data-testid="block-reason-input" />
                </div>
              </div>

              <Button type="submit" disabled={creating} className="rounded-full bg-[#B5704A] hover:bg-[#9C5E3D] text-white h-10" data-testid="create-block-button">
                <Plus className="w-4 h-4 mr-1" /> {creating ? "Creando…" : "Agregar bloqueo"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}
